import { Heart, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { useHealth } from '../hooks/useApi';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { clsx } from 'clsx';

function StatusIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
  ) : (
    <XCircle className="h-5 w-5 text-red-400" />
  );
}

export function HealthPage() {
  const { data, isLoading, error, refetch } = useHealth();

  if (isLoading) return <LoadingSpinner label="Sprawdzanie statusu..." />;
  if (error) return <ErrorAlert message={error.message} onRetry={() => refetch()} />;
  if (!data) return null;

  const statusConfig = {
    healthy: { label: 'Zdrowy', color: 'text-emerald-400', bg: 'bg-emerald-900/20', border: 'border-emerald-800/50', icon: CheckCircle2 },
    degraded: { label: 'Zdegradowany', color: 'text-amber-400', bg: 'bg-amber-900/20', border: 'border-amber-800/50', icon: AlertTriangle },
    unhealthy: { label: 'Niezdowy', color: 'text-red-400', bg: 'bg-red-900/20', border: 'border-red-800/50', icon: XCircle },
  };

  const cfg = statusConfig[data.status];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Heart className="h-6 w-6 text-brand-600 dark:text-brand-400" />
          Status systemu
        </h1>
        <p className="text-sm text-gray-500 mt-1">Kontrola zdrowia komponentow</p>
      </div>

      {/* Overall Status */}
      <div className={clsx('card flex items-center gap-4', cfg.bg, cfg.border)}>
        <cfg.icon className={clsx('h-10 w-10', cfg.color)} />
        <div>
          <p className={clsx('text-2xl font-bold', cfg.color)}>{cfg.label}</p>
          <p className="text-sm text-gray-500">Ostatnia kontrola: {new Date(data.timestamp).toLocaleString('pl-PL')}</p>
        </div>
      </div>

      {/* Individual checks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card flex items-center gap-4">
          <StatusIcon ok={data.checks.issuerDb} />
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Baza Issuer</p>
            <p className="text-xs text-gray-500">credentium-issuer</p>
          </div>
          <div className="ml-auto">
            {data.checks.issuerDb ? (
              <span className="badge-success">Polaczony</span>
            ) : (
              <span className="badge-danger">Brak polaczenia</span>
            )}
          </div>
        </div>

        <div className="card flex items-center gap-4">
          <StatusIcon ok={data.checks.walletDb} />
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Baza Wallet</p>
            <p className="text-xs text-gray-500">credentium-wallet</p>
          </div>
          <div className="ml-auto">
            {data.checks.walletDb ? (
              <span className="badge-success">Polaczony</span>
            ) : (
              <span className="badge-danger">Brak polaczenia</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
